/**
 * The demand model.
 *
 *   supply(cell) = sum over reachable venues of
 *                    categoryWeight * ratingFactor * openness * mealCurve * decay(travelTime)
 *
 *   drop(cell)   = residential * residentialWeight(t) + office * officeWeight(t)
 *
 *   score(cell)  = supply^ALPHA * drop^BETA
 *                    * dayMultiplier * weather * shabbat * exilePenalty
 *
 * What this model knows: where kitchens are, when they are open, what kind of food they
 * sell, how long they take to reach on a bike, and where the people who order live and
 * work.
 *
 * What it does not know, and cannot: how many other couriers are standing in the same
 * square. Earnings are demand divided by supply of riders, and only Wolt can see the
 * denominator. Read the output as "where the orders are", then cross-check against
 * Wolt's own hotspot overlay — which is effectively a courier-crowding map — and prefer
 * the cells that score well here and are *not* highlighted there.
 */

import { gridDisk } from 'h3-js';
import {
  decodeMask,
  getSlot,
  MINUTES_PER_SLOT,
  type Bundle,
  type WeekMoment,
} from '@tlv/shared';
import {
  ALPHA,
  BETA,
  CATEGORY_PROFILE,
  CATEGORY_WEIGHT,
  DAY_MULTIPLIER,
  DECAY_TAU_SECONDS,
  DEMAND_PROFILES,
  DROP_FLOOR,
  DROP_WEIGHTS,
  EXILE_PENALTY_STRENGTH,
  EXILE_RING_SIZE,
} from './weights.js';
import { buildOpenPriors, buildRatingScale, openProbability, type PriorsResult } from './priors.js';

/** Runtime conditions supplied by the modifier layer (weather, Hebrew calendar). */
export interface Conditions {
  /** From WEATHER in weights.ts. 1.0 when unknown or unremarkable. */
  weatherMultiplier: number;
  /** From SHABBAT in weights.ts. 1.0 outside the Shabbat and motzash windows. */
  shabbatMultiplier: number;
}

export const NEUTRAL_CONDITIONS: Conditions = {
  weatherMultiplier: 1,
  shabbatMultiplier: 1,
};

export interface ScoredCell {
  h3: string;
  /** Absolute model output. Comparable across times of day. */
  score: number;
  /**
   * Score as a fraction of the best cell *anywhere in the week*, clamped to 0..1.
   *
   * Deliberately not normalised against the best cell at the current moment. Doing that
   * makes every hour look equally busy, which hides the single most useful thing this
   * map can tell you: that Saturday at 11:00 is dead everywhere and you should still be
   * in bed. A dark map is a real answer.
   */
  normalized: number;
  /** Supply term before modifiers — useful for explaining a cell to the rider. */
  supply: number;
  /** Number of venues modelled as open and reachable. */
  openVenues: number;
}

/**
 * Precomputed, moment-independent parts of the model.
 *
 * Building the priors means decoding ~3000 bitmaps, so it happens once when the bundle
 * loads rather than on every tick of the time slider.
 */
export interface ScoringContext {
  bundle: Bundle;
  priors: PriorsResult;
  ratingFactor: (count: number | null) => number;
  densityByCell: Map<string, { residential: number; office: number }>;
  /** Best score any cell reaches at any sampled moment — the yardstick for `normalized`. */
  referenceScore: number;
}

/**
 * Hours sampled when calibrating the weekly reference.
 *
 * Chosen to bracket both peaks rather than to sample evenly: the lunch rush and the
 * dinner rush are what set the ceiling, and a uniform every-three-hours sweep can slide
 * between them and under-report the maximum.
 */
const REFERENCE_HOURS = [9, 12, 13, 14, 19, 20, 21, 22];

export function createScoringContext(bundle: Bundle): ScoringContext {
  const densityByCell = new Map<string, { residential: number; office: number }>();
  for (const cell of bundle.cells) {
    densityByCell.set(cell.h3, { residential: cell.residential, office: cell.office });
  }

  const context: ScoringContext = {
    bundle,
    priors: buildOpenPriors(bundle.venues),
    ratingFactor: buildRatingScale(bundle.venues),
    densityByCell,
    referenceScore: 1, // Replaced below; scoring needs a context to run at all.
  };

  // Calibrate against the whole week under neutral conditions, so that weather and
  // Shabbat surges read as genuinely above-normal rather than being normalised away.
  let best = 0;
  for (let day = 0; day < 7; day++) {
    for (const hour of REFERENCE_HOURS) {
      for (const cell of scoreCells(context, { day, minutesOfDay: hour * 60 })) {
        if (cell.score > best) best = cell.score;
      }
    }
  }
  context.referenceScore = best > 0 ? best : 1;

  return context;
}

/**
 * How strongly the office and residential layers count at this moment.
 *
 * Offices matter at weekday lunch and are close to irrelevant on a Friday night;
 * residential is the reverse but never drops to nothing, since people are home at
 * lunchtime too.
 */
export function dropWeights(moment: WeekMoment): { residential: number; office: number } {
  const isWorkday = moment.day >= 0 && moment.day <= 4; // Sunday–Thursday in Israel
  const inLunchWindow =
    moment.minutesOfDay >= DROP_WEIGHTS.officeLunchStartMin &&
    moment.minutesOfDay < DROP_WEIGHTS.officeLunchEndMin;

  const office = isWorkday && inLunchWindow ? 1 : DROP_WEIGHTS.officeBaseline;

  // Residential demand rises through the evening and stays high late.
  const hour = Math.floor(moment.minutesOfDay / 60);
  const eveningLift = hour >= 17 || hour < 2 ? 1 : DROP_WEIGHTS.residentialBaseline + 0.4;
  const residential = Math.max(DROP_WEIGHTS.residentialBaseline, eveningLift);

  return { residential, office };
}

/**
 * Threshold above which a venue with unknown hours is *reported* as open.
 *
 * The score uses the full fractional probability — a venue that is 30% likely to be
 * open contributes 30%, which is the mathematically right thing. But the venue count
 * shown to the rider is a claim about the world, and "117 open" alongside a score of 8
 * reads as a contradiction. Only reasonably confident venues are counted.
 */
const REPORTED_OPEN_THRESHOLD = 0.5;

interface VenueState {
  /** Expected order-emission rate, using the full fractional openness. */
  activity: number;
  /** Whether to count this venue in the rider-facing "open nearby" figure. */
  countAsOpen: boolean;
}

function venueActivity(
  context: ScoringContext,
  venueIdx: number,
  moment: WeekMoment,
  slot: number,
): VenueState {
  const venue = context.bundle.venues[venueIdx];
  if (!venue) return { activity: 0, countAsOpen: false };

  const openness =
    venue.hours === null
      ? openProbability(context.priors, venue.category, moment.day, slot)
      : getSlot(decodeMask(venue.hours), moment.day, slot)
        ? 1
        : 0;

  if (openness === 0) return { activity: 0, countAsOpen: false };

  const hour = Math.floor(moment.minutesOfDay / 60);

  // A scraped per-venue histogram beats the category average when it exists, which is
  // rarely — enrichment is optional and Google has no data for many small places.
  const scraped = venue.popularTimes?.[moment.day]?.[hour];
  const mealCurve =
    scraped !== undefined && scraped > 0
      ? scraped / 100
      : (DEMAND_PROFILES[CATEGORY_PROFILE[venue.category] ?? 'lunch_dinner']?.[hour] ?? 0);

  return {
    activity:
      CATEGORY_WEIGHT[venue.category] *
      context.ratingFactor(venue.ratingCount) *
      openness *
      mealCurve,
    countAsOpen: openness >= REPORTED_OPEN_THRESHOLD,
  };
}

/**
 * Score every cell for a moment in the week.
 *
 * Decoding each venue's bitmap on every call is wasteful when the same venue is
 * reachable from twenty cells, so activity is computed once per venue and reused.
 */
export function scoreCells(
  context: ScoringContext,
  moment: WeekMoment,
  conditions: Conditions = NEUTRAL_CONDITIONS,
): ScoredCell[] {
  const slot = Math.floor(moment.minutesOfDay / MINUTES_PER_SLOT);
  const { venues, reach } = context.bundle;

  const activity = new Float64Array(venues.length);
  const countAsOpen = new Uint8Array(venues.length);
  for (let i = 0; i < venues.length; i++) {
    const state = venueActivity(context, i, moment, slot);
    activity[i] = state.activity;
    countAsOpen[i] = state.countAsOpen ? 1 : 0;
  }

  // Pass 1 — supply.
  const supplyByCell = new Map<string, number>();
  const openCountByCell = new Map<string, number>();

  for (const cell of reach) {
    let supply = 0;
    let openVenues = 0;

    for (let i = 0; i < cell.venueIdx.length; i++) {
      const venueIdx = cell.venueIdx[i]!;
      const value = activity[venueIdx]!;
      if (value <= 0) continue;
      supply += value * Math.exp(-cell.seconds[i]! / DECAY_TAU_SECONDS);
      if (countAsOpen[venueIdx] === 1) openVenues++;
    }

    supplyByCell.set(cell.h3, supply);
    openCountByCell.set(cell.h3, openVenues);
  }

  // Pass 2 — the exile penalty needs neighbours' supply, so it cannot run in pass 1.
  const maxSupply = Math.max(...supplyByCell.values(), 1e-9);
  const drop = dropWeights(moment);
  const dayMultiplier = DAY_MULTIPLIER[moment.day] ?? 1;
  const globalMultiplier =
    dayMultiplier * conditions.weatherMultiplier * conditions.shabbatMultiplier;

  const scored: ScoredCell[] = [];

  for (const cell of reach) {
    const supply = supplyByCell.get(cell.h3) ?? 0;

    const density = context.densityByCell.get(cell.h3) ?? { residential: 0, office: 0 };
    const dropTerm = Math.max(
      DROP_FLOOR,
      density.residential * drop.residential + density.office * drop.office,
    );

    // How rich are the surroundings? A cell whose neighbours are empty will fling you
    // out of the action on most deliveries, and the ride back is unpaid.
    let neighbourSum = 0;
    let neighbourCount = 0;
    for (const neighbour of gridDisk(cell.h3, EXILE_RING_SIZE)) {
      const value = supplyByCell.get(neighbour);
      if (value === undefined) continue;
      neighbourSum += value;
      neighbourCount++;
    }
    const neighbourhoodRichness =
      neighbourCount > 0 ? neighbourSum / neighbourCount / maxSupply : 0;
    const exilePenalty =
      1 - EXILE_PENALTY_STRENGTH * (1 - Math.min(1, neighbourhoodRichness));

    const score =
      Math.pow(supply, ALPHA) * Math.pow(dropTerm, BETA) * globalMultiplier * exilePenalty;

    scored.push({
      h3: cell.h3,
      score,
      normalized: 0, // filled below
      supply,
      openVenues: openCountByCell.get(cell.h3) ?? 0,
    });
  }

  // Against the weekly reference, not against this moment's best cell — see ScoredCell.
  for (const cell of scored) {
    cell.normalized = Math.min(1, cell.score / context.referenceScore);
  }

  return scored;
}

/** The n best cells at a moment, highest first. */
export function topCells(scored: readonly ScoredCell[], n: number): ScoredCell[] {
  return [...scored].sort((a, b) => b.score - a.score).slice(0, n);
}
